import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { motion } from "framer-motion";
import { ArrowLeft, Plus, Sparkle } from "@phosphor-icons/react";

export function VoiceExamplesStep({ examples, onUpdateExample, guidance, onGuidanceChange, canAnalyze, onAnalyze, onBack }) {
  const [showExample2, setShowExample2] = useState(false);
  const [showExample3, setShowExample3] = useState(false);

  return (
    <motion.div key="examples" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} className="space-y-6" data-testid="voice-wizard-examples">
      <button onClick={onBack} className="flex items-center gap-1.5 text-sm text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors">
        <ArrowLeft size={16} /> Back
      </button>

      <div>
        <h2 className="text-xl font-bold text-[var(--text-primary)] font-['Cormorant_Garamond']">Paste your content examples</h2>
        <p className="text-sm text-[var(--text-muted)] mt-1">Each example should be at least 100 characters for best results.</p>
      </div>

      {/* Example 1 */}
      <div className="space-y-2" data-testid="example-panel-1">
        <label className="text-xs font-medium text-[var(--text-secondary)] uppercase tracking-wider">Example 1</label>
        <Textarea value={examples[0]} onChange={(e) => onUpdateExample(0, e.target.value)} placeholder="Paste an email, social post, bio, or any content that sounds like you..." className="min-h-[120px] bg-[var(--bg-card)] border-[var(--border-strong)] resize-y text-sm" data-testid="example-textarea-1" />
        <div className="flex items-center justify-between">
          <span className={`text-[10px] ${examples[0].length < 100 && examples[0].length > 0 ? "text-amber-400" : "text-[var(--text-muted)]"}`}>
            {examples[0].length} chars {examples[0].length > 0 && examples[0].length < 100 && "— aim for 100+ for best results"}
          </span>
        </div>
      </div>

      {/* Example 2 */}
      {!showExample2 ? (
        <button onClick={() => setShowExample2(true)} className="flex items-center gap-1.5 text-sm text-[#FF4400] hover:text-[#FF6633] transition-colors font-medium" data-testid="add-example-2-btn">
          <Plus size={14} /> Add another example
        </button>
      ) : (
        <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} className="space-y-2" data-testid="example-panel-2">
          <label className="text-xs font-medium text-[var(--text-secondary)] uppercase tracking-wider">Example 2</label>
          <Textarea value={examples[1]} onChange={(e) => onUpdateExample(1, e.target.value)} placeholder="Another piece of content that reflects your voice..." className="min-h-[120px] bg-[var(--bg-card)] border-[var(--border-strong)] resize-y text-sm" data-testid="example-textarea-2" />
          <span className={`text-[10px] ${examples[1].length < 100 && examples[1].length > 0 ? "text-amber-400" : "text-[var(--text-muted)]"}`}>
            {examples[1].length} chars {examples[1].length > 0 && examples[1].length < 100 && "— aim for 100+ for best results"}
          </span>
        </motion.div>
      )}

      {/* Example 3 */}
      {showExample2 && !showExample3 ? (
        <button onClick={() => setShowExample3(true)} className="flex items-center gap-1.5 text-sm text-[#FF4400] hover:text-[#FF6633] transition-colors font-medium" data-testid="add-example-3-btn">
          <Plus size={14} /> Add a third example
        </button>
      ) : showExample3 ? (
        <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} className="space-y-2" data-testid="example-panel-3">
          <label className="text-xs font-medium text-[var(--text-secondary)] uppercase tracking-wider">Example 3</label>
          <Textarea value={examples[2]} onChange={(e) => onUpdateExample(2, e.target.value)} placeholder="One more example helps improve accuracy..." className="min-h-[120px] bg-[var(--bg-card)] border-[var(--border-strong)] resize-y text-sm" data-testid="example-textarea-3" />
          <span className={`text-[10px] ${examples[2].length < 100 && examples[2].length > 0 ? "text-amber-400" : "text-[var(--text-muted)]"}`}>
            {examples[2].length} chars {examples[2].length > 0 && examples[2].length < 100 && "— aim for 100+ for best results"}
          </span>
        </motion.div>
      ) : null}

      {/* Guidance */}
      <div className="space-y-2 pt-2">
        <label className="text-xs font-medium text-[var(--text-secondary)] uppercase tracking-wider">Any specific guidance for the AI? <span className="text-[var(--text-muted)] normal-case">(optional)</span></label>
        <Input value={guidance} onChange={(e) => onGuidanceChange(e.target.value)} placeholder='e.g. "I keep things conversational, not corporate" or "I use humor but not sarcasm"' className="bg-[var(--bg-card)] border-[var(--border-strong)] text-sm" data-testid="guidance-input" />
      </div>

      {/* Analyze button */}
      <div className="flex justify-end pt-4">
        <Button onClick={onAnalyze} disabled={!canAnalyze} className="bg-[#FF4400] hover:bg-[#FF6633] text-white px-6 h-10 text-sm font-semibold disabled:opacity-40" data-testid="analyze-voice-btn">
          <Sparkle size={16} className="mr-2" /> Analyze My Voice
        </Button>
      </div>
    </motion.div>
  );
}
